import { readSessionUser } from '../accountAccess';

const REQUEST_TIMEOUT_MS = 45000;

function requiredString(value, label) {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    throw new Error(`${label} is missing. Please sign out and sign in again.`);
  }
  return normalized;
}

export function requireMyProfileSession(storage = window.sessionStorage) {
  const { user, error } = readSessionUser(storage);
  if (error) {
    throw new Error(error);
  }

  return {
    ...user,
    userEmail: requiredString(user?.userEmail, 'Your user email'),
    account_id: requiredString(user?.account_id, 'Your account'),
  };
}

export function buildMyProfilePayload(user, values = {}) {
  const accountId = requiredString(user?.account_id, 'Your account');
  const email = requiredString(user?.userEmail, 'Your user email');

  return {
    ...values,
    companyNumber: accountId,
    account_id: accountId,
    email,
  };
}

function rejectErrorPayload(data) {
  if (
    data
    && typeof data === 'object'
    && !Array.isArray(data)
    && typeof data.error === 'string'
  ) {
    throw new Error(data.error);
  }
}

export function requirePasswordMatch(data) {
  rejectErrorPayload(data);
  if (data !== 'Matched' && data !== 'Not_Matched') {
    throw new Error('Password verification returned an unexpected response.');
  }
  return data === 'Matched';
}

export function requireProfileSave(data) {
  rejectErrorPayload(data);
  if (data !== 'Success') {
    throw new Error('Profile update returned an unexpected response.');
  }
  return data;
}

export function requireSecurityAnswers(data) {
  rejectErrorPayload(data);
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Profile details returned an unexpected response.');
  }

  const fields = ['s_a1', 's_a2', 's_a3'];
  if (fields.some((field) => !Object.prototype.hasOwnProperty.call(data, field))) {
    throw new Error('Profile details returned an unexpected response.');
  }

  return Object.fromEntries(
    fields.map((field) => [field, String(data[field] ?? '')])
  );
}

export async function postMyProfile(
  client,
  apiBaseUrl,
  endpoint,
  payload,
  validate
) {
  const response = await client.post(
    `${apiBaseUrl}${endpoint}`,
    payload,
    { timeout: REQUEST_TIMEOUT_MS }
  );

  if (!response || typeof response.status !== 'number') {
    throw new Error('Profile request failed. Please try again.');
  }

  rejectErrorPayload(response.data);
  if (response.status < 200 || response.status >= 300) {
    throw new Error('Profile request failed. Please try again.');
  }
  return validate(response.data);
}

export function myProfileErrorMessage(error, fallback) {
  const responseError = error?.response?.data?.error;
  if (typeof responseError === 'string' && responseError.trim()) {
    return responseError;
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return fallback;
}
